import type {PressReleasePreview} from '@openapi/press-release';
import {clsx} from 'clsx';
import {formatIsoStringToJp} from '../../lib/format-iso-string-to-jp';
import {useMainImage} from './use-main-image';
import styles from './social-preview.module.css';

type Props = {
  readonly pressRelease: PressReleasePreview;
};

export function SocialPreview({
  pressRelease: {releaseCompleDate, title, companyName},
}: Props) {
  const mainImage = useMainImage();
  const releaseCompleDateJp =
    releaseCompleDate === null
      ? '配信時間未定'
      : formatIsoStringToJp(releaseCompleDate);
  const cardProps = {
    title: `${title} | ${companyName}のプレスリリース`,
    description: `${companyName}のプレスリリース（${releaseCompleDateJp}）${title}`,
    thumbnailUrl: mainImage?.url ?? 'https://prtimes.jp/common/pc_v4/og.png',
    thumbnailAlt: '',
  } satisfies Omit<CardProps, 'label' | 'size' | 'media'>;

  return (
    <div className={styles.container}>
      <div className={styles.inner}>
        <section className={styles.section}>
          <h2 className={styles.heading}>Facebook</h2>

          <div className={styles.cards}>
            {(
              [
                {size: 'lg', label: 'PC'},
                {size: 'md', label: 'SP'},
              ] as const
            ).map(({size, label}) => (
              <SummaryCard
                key={label}
                size={size}
                label={label}
                media='facebook'
                {...cardProps}
              />
            ))}
          </div>
        </section>

        <section className={styles.section}>
          <h2 className={styles.heading}>X</h2>

          <div className={styles.cards}>
            {(
              [
                {size: 'lg', label: 'PC Large'},
                {size: 'md', label: 'SP Large'},
              ] as const
            ).map(({size, label}) => (
              <SummaryCard
                key={label}
                size={size}
                label={label}
                media='x'
                {...cardProps}
              />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

type CardProps = {
  readonly label: string;
  readonly title: string;
  readonly description: string;
  readonly thumbnailUrl: string;
  readonly thumbnailAlt: string;
  readonly size: 'xs' | 'sm' | 'md' | 'lg';
  readonly media: 'facebook' | 'x';
};

function SummaryCard({
  label,
  size,
  title,
  description,
  thumbnailUrl,
  thumbnailAlt,
  media,
}: CardProps) {
  return (
    <div className={styles.cardContainer}>
      <p className={styles.label}>{label}</p>

      <article
        className={clsx(styles.card, styles.cardVariants)}
        data-size={size}
      >
        <img
          src={thumbnailUrl}
          alt={thumbnailAlt}
          className={styles.thumbnail}
          width={576}
          height={301}
        />
        <div
          className={clsx(styles.text, styles.textVariants)}
          data-size={size}
        >
          <p
            className={clsx(
              styles.multiLineClampBox,
              styles.singleLine,
              styles.titleVariants,
            )}
            data-size={size}
          >
            {title}
          </p>
          <p
            className={clsx(
              styles.descriptionVariants,
              media === 'x' && size === 'lg' && styles.descriptionLgX,
            )}
            data-size={size}
          >
            {description}
          </p>
        </div>
      </article>
    </div>
  );
}
